import React, { useState, useEffect, useRef } from 'react';
import { Search, Sparkles, MessageCircle, Book, User, Pill, Loader2 } from 'lucide-react';
import { SearchSuggestion } from '../types';

interface OmniSearchProps {
  onSearch: (query: string) => void;
  onSuggestionSelect: (suggestion: SearchSuggestion) => void;
  placeholder?: string;
  isLoading?: boolean;
}

const OmniSearch: React.FC<OmniSearchProps> = ({
  onSearch,
  onSuggestionSelect,
  placeholder = "Ask anything about health... 'Can I take paracetamol with ibuprofen?'",
  isLoading = false
}) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Mock AI suggestions based on query
  const generateSuggestions = (searchQuery: string): SearchSuggestion[] => {
    if (!searchQuery.trim()) return [];

    const mockSuggestions: SearchSuggestion[] = [
      {
        id: '1',
        text: 'Drug interactions between paracetamol and ibuprofen',
        type: 'drug',
        category: 'Drug Dictionary',
        relevance: 0.95
      },
      {
        id: '2',
        text: 'Ask Dr. Mensah about pain medication combinations',
        type: 'expert',
        category: 'Expert Consultation',
        relevance: 0.90
      },
      {
        id: '3',
        text: 'Safe pain relief during pregnancy - Health Cosmos',
        type: 'article',
        category: 'Health Articles',
        relevance: 0.85
      },
      {
        id: '4',
        text: 'Community discussion: Pain management experiences',
        type: 'community',
        category: 'Community Echoes',
        relevance: 0.80
      }
    ];

    return mockSuggestions.filter(s => 
      s.text.toLowerCase().includes(searchQuery.toLowerCase())
    ).sort((a, b) => b.relevance - a.relevance);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim()) {
        setIsThinking(true);
        // Simulate AI processing
        setTimeout(() => {
          setSuggestions(generateSuggestions(query));
          setIsThinking(false);
        }, 300);
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'drug': return <Pill className="text-blue-500" size={16} />;
      case 'expert': return <User className="text-green-500" size={16} />;
      case 'article': return <Book className="text-purple-500" size={16} />;
      case 'community': return <MessageCircle className="text-orange-500" size={16} />;
      default: return <Search className="text-gray-500" size={16} />;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      onSearch(query);
      setIsOpen(false);
    }
  };

  // Focus the input when the component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <div ref={searchRef} className="relative w-full max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            className="input-base pl-12 pr-12"
            disabled={isLoading}
          />
          {(isThinking || isLoading) && (
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
              <Loader2 className="text-green-500 animate-spin" size={20} />
            </div>
          )}
        </div>
      </form>

      {/* AI Suggestions Dropdown */}
      {isOpen && (query.trim() || suggestions.length > 0) && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-xl z-50 overflow-hidden animate-fade-in">
          {isThinking ? (
            <div className="p-6 text-center">
              <Sparkles className="mx-auto text-green-500 animate-spin mb-2" size={24} />
              <p className="text-gray-600">AI is thinking...</p>
            </div>
          ) : suggestions.length > 0 ? (
            <>
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Sparkles className="text-green-500" size={16} />
                  <span>AI Suggestions</span>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto scrollbar-custom">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion.id}
                    onClick={() => {
                      onSuggestionSelect(suggestion);
                      setIsOpen(false);
                      setQuery('');
                    }}
                    className="w-full p-4 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-b-0"
                  >
                    <div className="flex items-start space-x-3">
                      {getTypeIcon(suggestion.type)}
                      <div className="flex-1">
                        <p className="text-gray-900 font-medium">{suggestion.text}</p>
                        <p className="text-sm text-gray-500 mt-1">{suggestion.category}</p>
                      </div>
                      <div className="text-xs text-gray-400">
                        {Math.round(suggestion.relevance * 100)}% match
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="p-4 border-t border-gray-100 bg-gray-50">
                <button
                  onClick={() => {
                    onSearch(query);
                    setIsOpen(false);
                  }}
                  className="w-full text-center text-green-600 hover:text-green-700 font-medium"
                >
                  Ask anonymously in Community Echoes
                </button>
              </div>
            </>
          ) : query.trim() && (
            <div className="p-6 text-center">
              <p className="text-gray-600 mb-4">No suggestions found</p>
              <button
                onClick={() => {
                  onSearch(query);
                  setIsOpen(false);
                }}
                className="btn-primary"
              >
                Ask the Community
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OmniSearch;